import React, { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { RotateCcw, SlidersHorizontal } from 'lucide-react';
import { MultiSelectFilter } from '@/components/po-grn/MultiSelectFilter';
import { DateRangeFilter } from '@/components/po-grn/DateRangeFilter';

const PRIMARY = [
  ['doc_types', 'Document Type'], ['franchise_ids', 'Franchise ID'], ['franchise_names', 'Franchise / Store Name'],
  ['skus', 'Product ID / SKU'], ['product_names', 'Product Name'], ['categories', 'Category'], ['brands', 'Brand'],
  ['order_statuses', 'Current Order Status'], ['margin_statuses', 'Reported Margin Status'], ['severities', 'Exception Severity'],
];
const MORE = [
  ['customer_names', 'Customer Name'], ['store_types', 'Store Type / Segment'], ['districts', 'District / Town'], ['states', 'State'],
  ['invoice_numbers', 'Invoice / DC Number'], ['order_numbers', 'Sales Order Number'], ['payment_statuses', 'Payment Status'],
  ['return_statuses', 'Return Status'], ['sales_channels', 'Sales Channel'], ['dispatch_statuses', 'Dispatch Status'],
  ['warehouses', 'Warehouse / Dispatch Location'], ['created_by', 'Created By'], ['barcodes', 'Barcode'], ['hsns', 'HSN'],
  ['batches', 'Batch Number'], ['units', 'Unit of Measure'], ['gst_rates', 'GST %'], ['mrp_diff_statuses', 'MRP Difference Status'],
  ['dq_statuses', 'Price Data Quality Status'], ['exception_types', 'Exception Type'],
];

export const SAFilters = ({ filters, setFilters, options, onReset }) => {
  const [more, setMore] = useState(false);
  const pairs = options?.store_pairs || {};
  const linked = (k, v) => {
    if (k === 'franchise_ids') return { franchise_names: [...new Set(v.flatMap((id) => pairs[id] || []))] };
    if (k === 'franchise_names') return { franchise_ids: Object.keys(pairs).filter((id) => pairs[id].some((n) => v.includes(n))) };
    return {};
  };
  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v, ...linked(k, v) }));
  const moreActive = MORE.filter(([k]) => (filters[k] || []).length).length + (filters.prm_min || filters.prm_max ? 1 : 0);
  const sel = ([k, label]) => (
    <MultiSelectFilter key={k} label={label} options={options?.[k] || []} value={filters[k] || []}
      onChange={(v) => set(k, v)} testId={`sa-filter-${k.replace(/_/g, '-')}`} />
  );
  return (
    <div className="rounded-xl border border-orange-200/70 bg-white p-4 space-y-3" data-testid="sa-filters">
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <DateRangeFilter label="Document / Sales Date" from={filters.date_from} to={filters.date_to} preset={filters.date_preset}
          onChange={({ from, to, preset }) => setFilters((f) => ({ ...f, date_from: from, date_to: to, date_preset: preset, month: '' }))}
          testId="sa-filter-date" />
        {PRIMARY.map(sel)}
        <div className="flex items-end gap-2">
          <Button variant="outline" className="h-9 flex-1 gap-1 text-xs" onClick={() => setMore(!more)} data-testid="sa-more-filters-btn">
            <SlidersHorizontal size={13} /> More{moreActive ? ` (${moreActive})` : ''}
          </Button>
          <Button variant="outline" className="h-9 gap-1 text-xs" onClick={onReset} data-testid="sa-filters-reset-btn"><RotateCcw size={13} /></Button>
        </div>
      </div>
      {more && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 pt-3 border-t border-orange-100" data-testid="sa-more-filters">
          {MORE.map(sel)}
          <div className="flex flex-col gap-1">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Retailer Margin % Range</span>
            <div className="flex gap-1">
              <Input type="number" placeholder="Min" className="h-9" value={filters.prm_min ?? ''} onChange={(e) => set('prm_min', e.target.value)} data-testid="sa-filter-prm-min" />
              <Input type="number" placeholder="Max" className="h-9" value={filters.prm_max ?? ''} onChange={(e) => set('prm_max', e.target.value)} data-testid="sa-filter-prm-max" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
